import React, { useMemo, useRef, useEffect, useState } from 'react';
import { ChartConfig, ScientificChartConfig } from '../../../types';
import { DEFAULT_SCIENTIFIC_CONFIG, SCIENTIFIC_COLOR_PALETTES } from './config/scientificDefaults';
import { ScientificScatterPlot, ScientificLinePlot, ErrorBarPlot } from './components';
import { HeatmapRenderer } from '../../../components/HeatmapRenderer';
import { COLOR_PALETTES } from '../../../constants';

interface ScientificChartProps {
    config: ChartConfig;
    data: any[];
    onPointClick?: (point: any) => void;
    isVisible?: boolean;
}

/**
 * Main Scientific Chart Component
 * Routes to the appropriate plot type based on scientificConfig.plotType
 */
export const ScientificChart: React.FC<ScientificChartProps> = ({
    config,
    data,
    onPointClick,
    isVisible = true
}) => {
    // Merge config with defaults
    const scientificConfig = useMemo((): ScientificChartConfig => {
        const baseConfig = config.advanced?.scientific || config.scientificConfig;
        return {
            ...DEFAULT_SCIENTIFIC_CONFIG,
            ...baseConfig,
            // Deep merge nested objects
            fieldMappings: {
                ...DEFAULT_SCIENTIFIC_CONFIG.fieldMappings,
                ...(baseConfig?.fieldMappings || {}),
                // Also pull from top-level config for backward compatibility
                xField: baseConfig?.fieldMappings?.xField || config.xField,
                yField: baseConfig?.fieldMappings?.yField || config.yField,
                sizeField: baseConfig?.fieldMappings?.sizeField || config.sizeField
            },
            axisOptions: {
                ...DEFAULT_SCIENTIFIC_CONFIG.axisOptions,
                ...(baseConfig?.axisOptions || {})
            },
            analysis: {
                ...DEFAULT_SCIENTIFIC_CONFIG.analysis,
                ...(baseConfig?.analysis || {})
            },
            errorBars: {
                ...DEFAULT_SCIENTIFIC_CONFIG.errorBars,
                ...(baseConfig?.errorBars || {}),
                // Auto-enable error bars for 'errorbar' plot type
                ...(baseConfig?.plotType === 'errorbar' ? {
                    show: true,
                    mode: (baseConfig?.errorBars?.mode === 'none' || !baseConfig?.errorBars?.mode) ? 'constant' : baseConfig?.errorBars?.mode
                } : {})
            },
            transformations: {
                ...DEFAULT_SCIENTIFIC_CONFIG.transformations,
                ...(baseConfig?.transformations || {})
            },
            style: {
                ...DEFAULT_SCIENTIFIC_CONFIG.style,
                ...(baseConfig?.style || {})
            },
            legend: {
                ...DEFAULT_SCIENTIFIC_CONFIG.legend,
                ...(baseConfig?.legend || {})
            },
            animation: {
                ...DEFAULT_SCIENTIFIC_CONFIG.animation,
                ...(baseConfig?.animation || {})
            },
            heatmap: {
                ...DEFAULT_SCIENTIFIC_CONFIG.heatmap,
                ...(baseConfig?.heatmap || {})
            },
            vector: {
                ...DEFAULT_SCIENTIFIC_CONFIG.vector,
                ...(baseConfig?.vector || {})
            },
            threeD: {
                ...DEFAULT_SCIENTIFIC_CONFIG.threeD,
                ...(baseConfig?.threeD || {})
            }
        };
    }, [config]);

    // Debug logging for data flow
    console.log('[ScientificChart] Rendering:', {
        plotType: scientificConfig.plotType,
        dataLength: data?.length,
        xField: scientificConfig.fieldMappings?.xField,
        yField: scientificConfig.fieldMappings?.yField,
        sampleData: data?.[0],
        isVisible
    });

    if (!isVisible) return null;

    // Route to appropriate plot component
    const { plotType, subplots, fieldMappings } = scientificConfig;

    // Helper to render a single chart instance
    const renderSingleChart = (chartData: any[], instanceId?: number) => {
        switch (plotType) {
            case 'scatter':
                return (
                    <ScientificScatterPlot
                        config={config}
                        scientificConfig={scientificConfig}
                        data={chartData}
                        onPointClick={onPointClick}
                    />
                );

            case 'line':
                return (
                    <ScientificLinePlot
                        config={config}
                        scientificConfig={scientificConfig}
                        data={chartData}
                        onPointClick={onPointClick}
                    />
                );

            case 'errorbar':
                return (
                    <ErrorBarPlot
                        config={config}
                        scientificConfig={scientificConfig}
                        data={chartData}
                        onPointClick={onPointClick}
                    />
                );

            case 'heatmap':
                return (
                    <ResponsiveHeatmapWrapper
                        data={chartData}
                        config={config}
                        scientificConfig={scientificConfig}
                    />
                );

            case 'vector':
                return (
                    <VectorFieldPlot
                        data={chartData}
                        config={config}
                        scientificConfig={scientificConfig}
                    />
                );

            case '3d-scatter':
            case '3d-surface':
            case '3d-wireframe':
                return (
                    <ThreeDPlot
                        data={chartData}
                        config={config}
                        scientificConfig={scientificConfig}
                        plotType={plotType}
                    />
                );

            default:
                return (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <p>Unknown plot type: {plotType}</p>
                    </div>
                );
        }
    };

    // Subplot Logic
    const renderContent = () => {
        if (subplots.rows <= 1 && subplots.cols <= 1) {
            return renderSingleChart(data);
        }

        const count = subplots.rows * subplots.cols;
        const subCharts = [];

        // Logic to split data for subplots
        // 1. If Series Field exists, split by unique series
        // 2. Else if simple numeric slice (for large datasets)
        // 3. Else fallback to repeating (least desirable but safe) or equal slicing
        let splitData: any[][] = [];

        if (fieldMappings.seriesField) {
            // Facet by Series
            const uniqueSeries = Array.from(new Set(data.map(d => String(d[fieldMappings.seriesField!]))));
            // If more series than cells, we might group them or just show top N
            // If fewer, fill remaining cells with empty or wrap? 
            // Mapping distinct series to each cell
            splitData = Array(count).fill([]).map((_, i) => {
                if (i < uniqueSeries.length) {
                    const sKey = uniqueSeries[i];
                    return data.filter(d => String(d[fieldMappings.seriesField!]) === sKey);
                }
                return []; // Empty cell
            });
        } else {
            // No series field - split data equally (Data Parallelism / Sampling)
            // Useful for Heatmaps/Vectors/3D to show different 'sections' or just split the load
            const chunkSize = Math.ceil(data.length / count);
            splitData = Array(count).fill([]).map((_, i) => {
                return data.slice(i * chunkSize, (i + 1) * chunkSize);
            });
        }

        for (let i = 0; i < count; i++) {
            subCharts.push(
                <div key={i} className="relative w-full h-full border border-gold-500/10 overflow-hidden relative group">
                    {/* Small label for subplot index/series */}
                    <div className="absolute top-1 left-1 z-10 text-[9px] text-gold-500 bg-black/50 px-1 rounded pointer-events-none">
                        #{i + 1} {fieldMappings.seriesField ? `(${String(splitData[i]?.[0]?.[fieldMappings.seriesField!] || 'Empty')})` : ''}
                    </div>
                    {renderSingleChart(splitData[i], i)}
                </div>
            );
        }

        return (
            <div
                className="w-full h-full grid gap-1"
                style={{
                    gridTemplateRows: `repeat(${subplots.rows}, 1fr)`,
                    gridTemplateColumns: `repeat(${subplots.cols}, 1fr)`
                }}
            >
                {subCharts}
            </div>
        );
    };

    return (
        <div className="relative w-full h-full" style={{ minHeight: '350px' }}>
            <div className="absolute inset-0">
                {renderContent()}
            </div>
        </div>
    );
};

/**
 * Responsive Heatmap Wrapper with dimension tracking
 */
const ResponsiveHeatmapWrapper: React.FC<{
    data: any[];
    config: ChartConfig;
    scientificConfig: ScientificChartConfig;
}> = ({ data, config, scientificConfig }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 400, height: 300 }); // Start with minimum defaults

    useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                // Only update if we have valid dimensions
                if (rect.width > 0 && rect.height > 0) {
                    setDimensions({ width: rect.width, height: rect.height });
                }
            }
        };

        // Initial update with a small delay to ensure layout is complete
        const timer = setTimeout(updateDimensions, 50);

        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(containerRef.current);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
        };
    }, []);

    // Get palette from scientific config or use default
    const palette = COLOR_PALETTES[scientificConfig.style?.colormap as keyof typeof COLOR_PALETTES]
        || COLOR_PALETTES.viridis
        || ['#440154', '#482878', '#3e4989', '#31688e', '#26828e', '#1f9e89', '#35b779', '#6ece58', '#b5de2b', '#fde725'];

    // Merge scientificConfig into the config for HeatmapRenderer
    const mergedConfig: ChartConfig = {
        ...config,
        scientificConfig: scientificConfig,
        advanced: {
            ...config.advanced,
            scientific: scientificConfig
        }
    };

    console.log('[ResponsiveHeatmapWrapper] Rendering with dimensions:', dimensions, 'data:', data?.length);

    return (
        <div
            ref={containerRef}
            className="w-full h-full"
            style={{ minHeight: '200px', minWidth: '200px' }}
        >
            <HeatmapRenderer
                data={data}
                config={mergedConfig}
                palette={palette}
                width={dimensions.width}
                height={dimensions.height}
            />
        </div>
    );
};

/**
 * Vector Field Plot using Canvas with Zoom/Pan
 */
const VectorFieldPlot: React.FC<{
    data: any[];
    config: ChartConfig;
    scientificConfig: ScientificChartConfig;
}> = ({ data, config, scientificConfig }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

    // Zoom/Pan State
    const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
    const isDragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (!containerRef.current) return;
        const updateDimensions = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setDimensions({ width: width || 800, height: height || 600 });
            }
        };
        updateDimensions();
        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    // Handlers
    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const scaleBy = 1.1;
        const factor = e.deltaY < 0 ? scaleBy : 1 / scaleBy;
        setTransform(prev => ({
            ...prev,
            k: Math.max(0.1, Math.min(10, prev.k * factor))
        }));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => { isDragging.current = false; };

    // Axis formatter
    const formatAxis = (value: number, type: string) => {
        if (type === 'scientific') return value.toExponential(1);
        if (type === 'engineering') return value.toFixed(1) + 'e0';
        return value.toFixed(1);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !data || data.length === 0) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { width, height } = dimensions;
        const fieldMappings = scientificConfig.fieldMappings || {};
        const vectorConfig = scientificConfig.vector || {};
        const axisOptions = scientificConfig.axisOptions || {};

        // Clear canvas with dark background
        ctx.fillStyle = '#0a0a0f';
        ctx.fillRect(0, 0, width, height);

        ctx.save();
        // Apply Transform (Zoom/Pan) - center origin first?
        // Simple 2D camera transform
        ctx.translate(transform.x, transform.y);
        ctx.translate(width / 2, height / 2);
        ctx.scale(transform.k, transform.k);
        ctx.translate(-width / 2, -height / 2);

        const xField = fieldMappings.xField || 'x';
        const yField = fieldMappings.yField || 'y';
        const uField = fieldMappings.uField || 'u';
        const vField = fieldMappings.vField || 'v';

        const isLogX = axisOptions.logScaleX;
        const isLogY = axisOptions.logScaleY;

        const getScaleVal = (val: number, isLog: boolean) => {
            if (!isLog) return val;
            return val > 0 ? Math.log10(val) : NaN;
        };

        let validData = data.filter(d => {
            const x = Number(d[xField]);
            const y = Number(d[yField]);
            if (isNaN(x) || isNaN(y)) return false;
            if (isLogX && x <= 0) return false;
            if (isLogY && y <= 0) return false;
            return true;
        });

        if (validData.length === 0) {
            ctx.restore();
            ctx.fillStyle = '#888';
            ctx.fillText('No valid data', width / 2, height / 2);
            return;
        }

        const xVals = validData.map(d => getScaleVal(Number(d[xField]), isLogX));
        const yVals = validData.map(d => getScaleVal(Number(d[yField]), isLogY));
        const xMin = Math.min(...xVals), xMax = Math.max(...xVals);
        const yMin = Math.min(...yVals), yMax = Math.max(...yVals);

        const margin = 40;
        const plotWidth = width - margin * 2;
        const plotHeight = height - margin * 2;

        const scaleX = (val: number) => margin + ((val - xMin) / (xMax - xMin || 1)) * plotWidth;
        const scaleY = (val: number) => height - margin - ((val - yMin) / (yMax - yMin || 1)) * plotHeight;

        // Max magnitude
        const magnitudes = validData.map(d => Math.hypot(Number(d[uField]), Number(d[vField])));
        const maxMag = Math.max(...magnitudes, 0.001);

        // Draw Grid
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 0.5 / transform.k; // Constant line width
        const xTicks = 5, yTicks = 5;
        for (let i = 0; i <= xTicks; i++) {
            const gx = margin + (plotWidth / xTicks) * i;
            ctx.beginPath(); ctx.moveTo(gx, margin); ctx.lineTo(gx, height - margin); ctx.stroke();
            // Label
            const val = xMin + (i / xTicks) * (xMax - xMin);
            ctx.fillStyle = '#666'; ctx.font = `${10 / transform.k}px sans-serif`;
            ctx.fillText(formatAxis(isLogX ? Math.pow(10, val) : val, axisOptions.axisFormat!), gx, height - margin + 15 / transform.k);
        }
        for (let i = 0; i <= yTicks; i++) {
            const gy = margin + (plotHeight / yTicks) * i;
            ctx.beginPath(); ctx.moveTo(margin, gy); ctx.lineTo(width - margin, gy); ctx.stroke();
            // Label
            const val = yMin + ((yTicks - i) / yTicks) * (yMax - yMin);
            ctx.fillStyle = '#666'; ctx.font = `${10 / transform.k}px sans-serif`;
            ctx.fillText(formatAxis(isLogY ? Math.pow(10, val) : val, axisOptions.axisFormat!), margin - 25 / transform.k, gy);
        }

        // Draw Vectors
        const arrowScale = ((vectorConfig as any).scale || 1) * 20;
        const arrowHead = ((vectorConfig as any).arrowSize || 1) * 4;

        validData.forEach((d) => {
            // ... (Same vector drawing logic, but adjusted for log/zoom if needed)
            // ... Copying preserved logic
            const x = getScaleVal(Number(d[xField]), isLogX);
            const y = getScaleVal(Number(d[yField]), isLogY);
            let u = Number(d[uField]) || 0;
            let v = Number(d[vField]) || 0;

            if ((vectorConfig as any).normalize && maxMag > 0) {
                const m = Math.hypot(u, v);
                if (m > 0) { u /= m; v /= m; }
            }

            const px = scaleX(x);
            const py = scaleY(y);
            const dx = u * arrowScale;
            const dy = -v * arrowScale;

            const mag = Math.hypot(u, v);
            // Use colormap palette for magnitude coloring
            const colormapName = (scientificConfig?.style as any)?.colormap || 'weaverGold';
            const palette = SCIENTIFIC_COLOR_PALETTES[colormapName as keyof typeof SCIENTIFIC_COLOR_PALETTES]
                || SCIENTIFIC_COLOR_PALETTES.weaverGold;
            const ratio = maxMag > 0 ? Math.max(0, Math.min(1, mag / maxMag)) : 0.5;
            const idx = Math.min(Math.floor(ratio * (palette.length - 1)), palette.length - 1);
            const vectorColor = palette[Math.max(0, idx)];

            ctx.strokeStyle = vectorColor;
            ctx.fillStyle = ctx.strokeStyle;
            ctx.lineWidth = 1.5 / transform.k;

            ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + dx, py + dy); ctx.stroke();

            // Arrowhead
            const angle = Math.atan2(dy, dx);
            const ah = arrowHead / transform.k; // Scale arrowhead so it doesn't get huge
            ctx.beginPath();
            ctx.moveTo(px + dx, py + dy);
            ctx.lineTo(px + dx - ah * Math.cos(angle - Math.PI / 6), py + dy - ah * Math.sin(angle - Math.PI / 6));
            ctx.lineTo(px + dx - ah * Math.cos(angle + Math.PI / 6), py + dy - ah * Math.sin(angle + Math.PI / 6));
            ctx.closePath();
            ctx.fill();
        });

        ctx.restore();

        // Overlay UI
        ctx.fillStyle = '#fff';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`Zoom: ${transform.k.toFixed(1)}x`, width - 10, 20);

    }, [data, dimensions, transform, scientificConfig]);

    return (
        <div ref={containerRef} className="w-full h-full cursor-move" onWheel={handleWheel} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
            <canvas ref={canvasRef} width={dimensions.width} height={dimensions.height} className="w-full h-full" />
        </div>
    );
};



/**
 * 3D Plot using Canvas with rotation
 */
const ThreeDPlot: React.FC<{
    data: any[];
    config: ChartConfig;
    scientificConfig: ScientificChartConfig;
    plotType: string;
}> = ({ data, config, scientificConfig, plotType }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

    // Get threeD config values
    const threeD = scientificConfig.threeD || {};

    // Initialize rotation from config, sync when config changes
    const [rotation, setRotation] = useState({
        azimuth: (threeD as any).azimuth ?? 45,
        elevation: (threeD as any).elevation ?? 30
    });

    // Add zoom state for 3D view
    const [zoom, setZoom] = useState(1);

    // Sync rotation when threeD config changes (from slider controls)
    useEffect(() => {
        const configAzimuth = (threeD as any).azimuth;
        const configElevation = (threeD as any).elevation;
        if (typeof configAzimuth === 'number' && typeof configElevation === 'number') {
            setRotation({ azimuth: configAzimuth, elevation: configElevation });
        }
    }, [(threeD as any).azimuth, (threeD as any).elevation]);

    const isDragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    // Handle mouse wheel for zoom
    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const scaleBy = 1.1;
        const factor = e.deltaY < 0 ? scaleBy : 1 / scaleBy;
        setZoom(prev => Math.max(0.2, Math.min(5, prev * factor)));
    };

    useEffect(() => {
        if (!containerRef.current) return;
        const updateDimensions = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setDimensions({ width: width || 800, height: height || 600 });
            }
        };
        updateDimensions();
        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    // Handle mouse rotation
    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        setRotation(prev => ({
            azimuth: prev.azimuth + dx * 0.5,
            elevation: Math.max(-90, Math.min(90, prev.elevation - dy * 0.5))
        }));
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    // Axis formatter
    const formatAxis = (value: number, type: string) => {
        if (type === 'scientific') return value.toExponential(1);
        if (type === 'engineering') {
            const exp = Math.floor(Math.log10(Math.abs(value)) / 3) * 3;
            if (exp === 0 || !isFinite(exp)) return value.toFixed(1);
            return `${(value / Math.pow(10, exp)).toFixed(1)}e${exp}`;
        }
        return value.toFixed(1);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !data || data.length === 0) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { width, height } = dimensions;
        const fieldMappings = scientificConfig.fieldMappings || {};
        const threeD = scientificConfig.threeD || {};
        const axisOptions = scientificConfig.axisOptions || {};
        const style = scientificConfig.style || {};

        // Clear canvas
        ctx.fillStyle = '#0a0a0f';
        ctx.fillRect(0, 0, width, height);

        const xField = fieldMappings.xField || 'x';
        const yField = fieldMappings.yField || 'y';
        const zField = fieldMappings.zField || 'z';
        const cField = fieldMappings.colorField;
        const sField = fieldMappings.sizeField; // Size field

        const isLogX = axisOptions.logScaleX;
        const isLogY = axisOptions.logScaleY;
        const isLogZ = axisOptions.logScaleZ;

        // Helper to transform value based on scale type
        const getScaleVal = (val: number, isLog: boolean) => {
            if (!isLog) return val;
            return val > 0 ? Math.log10(val) : NaN;
        };

        // Extract 3D points
        const points = data.map(d => {
            const rawX = Number(d[xField]);
            const rawY = Number(d[yField]);
            const rawZ = Number(d[zField]);
            const valC = cField ? Number(d[cField]) : NaN;
            const valS = sField ? Number(d[sField]) : 1;

            // Filter invalid log values
            if (isLogX && rawX <= 0) return null;
            if (isLogY && rawY <= 0) return null;
            if (isLogZ && rawZ <= 0) return null;

            return {
                x: getScaleVal(rawX, isLogX),
                y: getScaleVal(rawY, isLogY),
                z: getScaleVal(rawZ, isLogZ),
                c: valC,
                s: valS,
                rawC: valC
            };
        }).filter(p => p && !isNaN(p.x) && !isNaN(p.y) && !isNaN(p.z));

        if (points.length === 0) {
            ctx.fillStyle = '#888';
            ctx.font = '14px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('No valid 3D data', width / 2, height / 2);
            return;
        }

        // Find ranges
        const xs = points.map(p => p!.x);
        const ys = points.map(p => p!.y);
        const zs = points.map(p => p!.z);
        const cs = points.map(p => p!.c).filter(c => !isNaN(c));
        const ss = points.map(p => p!.s).filter(s => !isNaN(s));

        const xMin = Math.min(...xs), xMax = Math.max(...xs);
        const yMin = Math.min(...ys), yMax = Math.max(...ys);
        const zMin = Math.min(...zs), zMax = Math.max(...zs);
        const cMin = cs.length ? Math.min(...cs) : 0;
        const cMax = cs.length ? Math.max(...cs) : 1;
        const sMin = ss.length ? Math.min(...ss) : 0;
        const sMax = ss.length ? Math.max(...ss) : 1;

        // Normalize to -1 to 1
        const normalize = (val: number, min: number, max: number) =>
            max === min ? 0 : ((val - min) / (max - min) - 0.5) * 2;

        const project = (x: number, y: number, z: number) => {
            const az = (rotation.azimuth * Math.PI) / 180;
            const el = (rotation.elevation * Math.PI) / 180;

            const x1 = x * Math.cos(az) - z * Math.sin(az);
            const z1 = x * Math.sin(az) + z * Math.cos(az);
            const y1 = y * Math.cos(el) - z1 * Math.sin(el);
            const z2 = y * Math.sin(el) + z1 * Math.cos(el);

            const projectionType = (threeD as any).projection || 'perspective';
            const perspective = projectionType === 'orthographic' ? 1 : (3 / (3 + z2));
            const scale = Math.min(width, height) * 0.35 * zoom; // Apply zoom

            return {
                px: width / 2 + x1 * scale * perspective,
                py: height / 2 - y1 * scale * perspective,
                depth: z2
            };
        };

        // Helper to get color from selected colormap
        const getColor = (val: number) => {
            if (isNaN(val)) return '#f59e0b'; // Default gold color

            // Get the colormap palette from config
            const colormapName = (style as any).colormap || 'weaverGold';
            const palette = SCIENTIFIC_COLOR_PALETTES[colormapName as keyof typeof SCIENTIFIC_COLOR_PALETTES]
                || SCIENTIFIC_COLOR_PALETTES.weaverGold;

            const ratio = Math.max(0, Math.min(1, (val - cMin) / (cMax - cMin || 1)));
            const idx = Math.min(Math.floor(ratio * (palette.length - 1)), palette.length - 1);
            return palette[Math.max(0, idx)];
        };

        // Draw Axes & Ticks
        const drawAxisSystem = () => {
            const origin = project(0, 0, 0); // Center
            // We draw axis lines from -1 to 1 normalized, or 0 to 1?
            // Let's draw the box edges

            ctx.strokeStyle = '#333';
            ctx.lineWidth = 0.5;
            if (axisOptions.grid || axisOptions.minorGrid) { // Floor grid
                for (let i = -1; i <= 1; i += 0.5) {
                    const s = project(i, -1, -1); const e = project(i, -1, 1);
                    ctx.beginPath(); ctx.moveTo(s.px, s.py); ctx.lineTo(e.px, e.py); ctx.stroke();
                    const s2 = project(-1, -1, i); const e2 = project(1, -1, i);
                    ctx.beginPath(); ctx.moveTo(s2.px, s2.py); ctx.lineTo(e2.px, e2.py); ctx.stroke();
                }
            }

            // Major Axes with Ticks
            const axes = [
                { start: [-1, -1, -1], end: [1.2, -1, -1], color: '#ef4444', label: xField, min: xMin, max: xMax },
                { start: [-1, -1, -1], end: [-1, 1.2, -1], color: '#22c55e', label: yField, min: yMin, max: yMax },
                { start: [-1, -1, -1], end: [-1, -1, 1.2], color: '#3b82f6', label: zField, min: zMin, max: zMax }
            ];

            axes.forEach(axis => {
                const s = project(axis.start[0], axis.start[1], axis.start[2]);
                const e = project(axis.end[0], axis.end[1], axis.end[2]);

                ctx.beginPath(); ctx.moveTo(s.px, s.py); ctx.lineTo(e.px, e.py);
                ctx.strokeStyle = axis.color; ctx.lineWidth = 1.5; ctx.stroke();

                ctx.fillStyle = axis.color; ctx.font = '12px sans-serif';
                ctx.fillText(axis.label, e.px, e.py);

                // Ticks
                for (let t = 0; t <= 1; t += 0.25) {
                    // Interpolate position (mapped from -1 to 1 range relative to axis length)
                    const range = axis.max - axis.min;
                    const val = axis.min + t * range;
                    // 3D pos
                    // Only logic for X axis as example, generalizing needs vector match
                    // Simplified: just put 2 ticks at start/end
                }
                // Draw Min/Max labels
                const txtMin = formatAxis(axis.min, axisOptions.axisFormat);
                const txtMax = formatAxis(axis.max, axisOptions.axisFormat);
                ctx.fillStyle = '#888'; ctx.font = '10px sans-serif';
                ctx.fillText(txtMin, s.px + 10, s.py + 10);
                const eReal = project(axis.end[0] * 0.8, axis.end[1] * 0.8, axis.end[2] * 0.8); // Approx end of data range
                ctx.fillText(txtMax, eReal.px + 10, eReal.py + 10);
            });
        };

        drawAxisSystem();

        // Sort points
        const projectedPoints = points.map(p => {
            const norm = {
                x: normalize(p!.x, xMin, xMax),
                y: normalize(p!.y, yMin, yMax),
                z: normalize(p!.z, zMin, zMax)
            };
            const proj = project(norm.x, norm.y, norm.z);

            // Correct Color Logic: Check for null/NaN explicitly, not falsy (0 is valid)
            const hasColor = p!.c !== undefined && p!.c !== null && !isNaN(p!.c);
            const pColor = hasColor ? getColor(p!.c) : null;

            return { ...p, ...proj, norm, color: pColor };
        }).sort((a, b) => a.depth - b.depth);

        // Draw Shape Helper
        const drawShape = (px: number, py: number, size: number, shape: string, color: string, hasOutline: boolean) => {
            ctx.fillStyle = color;
            ctx.beginPath();
            if (shape === 'square') {
                ctx.rect(px - size, py - size, size * 2, size * 2);
            } else if (shape === 'triangle') {
                ctx.moveTo(px, py - size);
                ctx.lineTo(px + size, py + size);
                ctx.lineTo(px - size, py + size);
                ctx.closePath();
            } else if (shape === 'cross') {
                ctx.moveTo(px - size, py - size); ctx.lineTo(px + size, py + size);
                ctx.moveTo(px + size, py - size); ctx.lineTo(px - size, py + size);
                ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.stroke(); return;
            } else if (shape === 'diamond') {
                ctx.moveTo(px, py - size); ctx.lineTo(px + size, py);
                ctx.lineTo(px, py + size); ctx.lineTo(px - size, py);
                ctx.closePath();
            } else {
                ctx.arc(px, py, size, 0, Math.PI * 2);
            }
            ctx.fill();

            if (hasOutline) {
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 1;
                ctx.stroke();
            }
        };

        // Draw points
        const baseSize = (scientificConfig.style?.markerSize as number) || 4;
        const markerShape = (scientificConfig.style?.markerShape as string) || 'circle';
        const hasOutline = !!(scientificConfig.style?.markerOutline);

        console.log('[ThreeDPlot] Drawing with markerShape:', markerShape, 'baseSize:', baseSize);

        projectedPoints.forEach((p, i) => {
            let color = p.color;
            if (!color) {
                // Default to WeaverBI gold with depth shading
                const depthNorm = (p.depth + 2) / 4; // 0 to 1
                // Use brighter gold colors: minimum 50%, max 85%
                const brightness = (threeD as any).depthShading ? 50 + depthNorm * 35 : 65;
                color = `hsl(35, 100%, ${brightness}%)`;
            }

            // Size calculation
            let size = baseSize;
            if (sField && sMax > sMin) {
                const sRatio = (p.s - sMin) / (sMax - sMin);
                size = baseSize * (0.5 + sRatio * 2.5);
            }

            // 3D Scatter: Draw points only
            if (plotType === '3d-scatter') {
                drawShape(p.px, p.py, size, markerShape, color!, hasOutline);
            }

            // 3D Surface: Draw filled faces with connections
            if (plotType === '3d-surface') {
                // Draw filled polygon between nearby points
                if (i > 0) {
                    const prev = projectedPoints[i - 1];
                    // Draw a filled triangle/quad face
                    ctx.fillStyle = color!;
                    ctx.globalAlpha = 0.6;
                    ctx.beginPath();
                    ctx.moveTo(prev.px, prev.py);
                    ctx.lineTo(p.px, p.py);
                    // Create a small offset to make it look like a surface
                    ctx.lineTo(p.px + (p.px - prev.px) * 0.1, p.py + 3);
                    ctx.lineTo(prev.px + (p.px - prev.px) * 0.1, prev.py + 3);
                    ctx.closePath();
                    ctx.fill();
                    ctx.globalAlpha = 1.0;
                }
                // Draw connected lines with thicker stroke
                if (i > 0) {
                    const prev = projectedPoints[i - 1];
                    ctx.strokeStyle = color!;
                    ctx.lineWidth = 1.5;
                    ctx.beginPath();
                    ctx.moveTo(prev.px, prev.py);
                    ctx.lineTo(p.px, p.py);
                    ctx.stroke();
                }
                // Small point markers
                ctx.fillStyle = color!;
                ctx.beginPath();
                ctx.arc(p.px, p.py, size * 0.5, 0, Math.PI * 2);
                ctx.fill();
            }

            // 3D Wireframe: Draw only lines (no fill, no points)
            if (plotType === '3d-wireframe') {
                if (i > 0) {
                    const prev = projectedPoints[i - 1];
                    // Use a distinct wireframe color
                    ctx.strokeStyle = color || '#00ff88';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(prev.px, prev.py);
                    ctx.lineTo(p.px, p.py);
                    ctx.stroke();
                }
                // Draw small vertex indicators for wireframe
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.arc(p.px, p.py, 2, 0, Math.PI * 2);
                ctx.fill();
            }
        });

        // Interaction hint
        ctx.fillStyle = '#555';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`Rot: ${Math.round(rotation.azimuth)}°, ${Math.round(rotation.elevation)}° | Zoom: ${zoom.toFixed(1)}x`, width - 10, height - 10);

    }, [data, dimensions, rotation, zoom, scientificConfig, plotType]);

    return (
        <div
            ref={containerRef}
            className="w-full h-full cursor-grab active:cursor-grabbing z-10"
            style={{ touchAction: 'none', userSelect: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
        >
            <canvas
                ref={canvasRef}
                width={dimensions.width}
                height={dimensions.height}
                className="w-full h-full"
            />
        </div>
    );
};

export default ScientificChart;

